import { Injectable } from '@angular/core';
import { MACHINE_ID_COOKIE_NAME } from './constants';
import { AppViewMode, SettingsService } from './settings.service';
import { DeviceDetectionService } from './device-detection.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserMachineIdService {
  constructor(
    private deviceDetectionService: DeviceDetectionService,
    private settingsService: SettingsService
  ) {}

  /**
   * The user can be identified if the cookie in_machine_id is set.
   * @returns
   */
  hasMachineId(): boolean {
    return !!this.getMachineIdFromCookie();
  }

  /**
   * Check whether the machineID cookie and the one passed on the URL are the same.
   * If not, the user could have had the PWA link shared with them and even in this case we want them to go through the redirection,
   * so we can log the scan event.
   */
  isMachineIdSynced() {
    const machineIdFromUrl = this.settingsService.settings.machineId;
    const machineIdFromCookie = this.getMachineIdFromCookie();

    if (machineIdFromUrl && machineIdFromCookie) {
      return machineIdFromUrl === machineIdFromCookie;
    }

    return false;
  }

  /**
   * All the PWA visits should be coming from the tap URL.
   * If that is not the case (for example, if the user has been shared a link to the PWA),
   * the user does not have a machineId cookie set in the browser, which cause all sorts of issues (e.g. quiz/survey cannot save responses, golden ticket cannot allocate prizes).
   *
   * To solve for this, we check if the machineId cookie is set and if not, we redirect the user to the TAP URL with the tagId.
   *
   */
  checkForMachineIdOrRedirect() {
    if (
      this.settingsService.settings.viewMode === AppViewMode.DEFAULT &&
      (!this.hasMachineId() || !this.isMachineIdSynced()) &&
      this.deviceDetectionService.isMobile()
    ) {
      if (this.canBeIdentified()) {
        // Set machine id cookie for preview mode
        const tagId = this.settingsService.settings.tagId;
        if (tagId) {
          // redirect to the tagId URL
          console.log(`Redirecting to TAP URL because machineId is not set.`);
          window.location.href = `${environment.TAP_URL}/${tagId}`;
        } else {
          console.error(
            `The client does not have a machineId cookie but we cannot redirect to the TAP URL because the tagId is not set.`
          );
        }
      } else {
        // Cookies are disabled, show an error message or take appropriate action
        console.error(
          'Cookies are disabled. Unable to set machineId cookie. Cannot redirect to TAP URL but will still render PWA.'
        );
      }
    }
  }

  private getMachineIdFromCookie(): string | null {
    const cookieValue = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${MACHINE_ID_COOKIE_NAME}=`));

    if (cookieValue) {
      return cookieValue.split('=')[1];
    }
    return null;
  }

  /**
   * We identify the user by the cookie in_machine_id.
   * If the cookies cannot be set on the user browser, for example, if the user has cookies disabled,
   * we cannot identify the user.
   *
   * @returns
   */
  private canBeIdentified(): boolean {
    // Step 1: Check if cookies are enabled by trying to set a test cookie
    try {
      document.cookie = 'cookie_test=1; path=/';
      const cookiesEnabled = document.cookie.includes('cookie_test=1');

      // Clean up the test cookie
      document.cookie = 'cookie_test=; Max-Age=0; path=/';

      if (!cookiesEnabled) {
        console.warn('Cookies are disabled. Skipping TAP redirect.');
        return false;
      } else {
        return true;
      }
    } catch (e) {
      console.warn('Error accessing cookies. Skipping TAP redirect.', e);
      return false;
    }
  }
}
